// ============================================================
// BIOGAVAL-NEO 2018 (INVASSAT) — Motor de cálculo de riesgo biológico
// Fórmula: R = G + T + P + F - V - MH
// NAB = 8 (nivel de acción biológica) | LEB = 12 (límite de exposición biológica)
// Validado contra los 2 casos prácticos del Anexo II del manual original.
// ============================================================

// ===== Catálogo de agentes (refleja tabla biological_agents) =====
export type ViaTransmision = 'indirecta' | 'directa' | 'aerea';

export interface BiologicalAgent {
  id: string;
  name: string;
  grupo: 1 | 2 | 3 | 4;
  vias_transmision: ViaTransmision[];
  sector_tipico?: string;
  notes?: string;
}

// ===== Exposición evaluada por puesto (refleja job_position_biological_exposure) =====
export type ViaProbabilidad = 'prevalencia' | 'incidencia';

export interface BiologicalExposureInput {
  agent_id: string;
  via_probabilidad: ViaProbabilidad;
  valor_probabilidad: number; // % si prevalencia, tasa/100.000 hab si incidencia
  frecuencia_pct: number;     // 0-100, % de tiempo en tareas de riesgo
  vacunacion_pct: number;     // 0-100, % personal vacunado o protegido (0 si no hay vacuna eficaz)
}

// ===== Cuestionario de medidas higiénicas (42 ítems, PDF sección 2.4) =====
// El campo "sectores" es orientativo (ayuda de UI); la aplicabilidad real la
// decide el evaluador marcando 'no_aplicable' por ítem (así lo indica el propio manual, 2.1).
export type SectorMH = 'A' | 'S' | 'L' | 'E' | 'D' | 'T';
// A=Animales, S=Sanidad, L=Alimentos, E=Residuos, D=Depuradoras, T=Todas las actividades

export interface HygieneItem {
  id: string;
  label: string;
  sectores: SectorMH[];
}

export type HygieneResponse = 'si' | 'no' | 'no_aplicable';
export interface HygieneAnswers {
  [item_id: string]: HygieneResponse;
}

export const HYGIENE_ITEMS_CATALOG: HygieneItem[] = [
  { id: 'MH-01', label: "Dispone de ropa de trabajo", sectores: ['T'] },
  { id: 'MH-02', label: "Uso de ropa de trabajo", sectores: ['T'] },
  { id: 'MH-03', label: "Dispone de EPI's", sectores: ['T'] },
  { id: 'MH-04', label: "Se limpian los EPI's", sectores: ['T'] },
  { id: 'MH-05', label: "Se dispone de lugar para almacenar EPI's", sectores: ['T'] },
  { id: 'MH-06', label: "Se controla el correcto funcionamiento de EPI's", sectores: ['T'] },
  { id: 'MH-07', label: "Limpieza de ropa de trabajo por el empresariado", sectores: ['T'] },
  { id: 'MH-08', label: "Se dispone de doble taquilla", sectores: ['T'] },
  { id: 'MH-09', label: "Se dispone de aseos", sectores: ['S', 'L', 'E', 'D'] },
  { id: 'MH-10', label: "Se dispone de duchas", sectores: ['S', 'L', 'E', 'D'] },
  { id: 'MH-11', label: "Se dispone de sistema para lavado de manos", sectores: ['S', 'L', 'E', 'D'] },
  { id: 'MH-12', label: "Se dispone de sistema para lavado de ojos", sectores: ['S', 'E', 'D'] },
  { id: 'MH-13', label: "Se prohíbe comer o beber", sectores: ['T'] },
  { id: 'MH-14', label: "Se prohíbe fumar", sectores: ['T'] },
  { id: 'MH-15', label: "Se dispone de tiempo para el aseo antes de abandonar la zona de riesgo dentro de la jornada", sectores: ['T'] },
  { id: 'MH-16', label: "Suelos y paredes fáciles de limpiar", sectores: ['S', 'L'] },
  { id: 'MH-17', label: "Los suelos y paredes están suficientemente limpios", sectores: ['S', 'L'] },
  { id: 'MH-18', label: "Hay métodos de limpieza de equipos de trabajo", sectores: ['T'] },
  { id: 'MH-19', label: "Se aplican procedimientos de desinfección", sectores: ['A', 'S', 'L', 'E', 'D'] },
  { id: 'MH-20', label: "Se aplican procedimientos de desinsectación", sectores: ['A', 'S', 'L', 'E', 'D'] },
  { id: 'MH-21', label: "Se aplican procedimientos de desratización", sectores: ['A', 'S', 'L', 'E', 'D'] },
  { id: 'MH-22', label: "Hay ventilación general con renovación de aire", sectores: ['S', 'L'] },
  { id: 'MH-23', label: "Hay mantenimiento del sistema de ventilación", sectores: ['S', 'L'] },
  { id: 'MH-24', label: "Existe material de primeros auxilios en cantidad suficiente (Anexo VI Real Decreto 486/97)", sectores: ['T'] },
  { id: 'MH-25', label: "Se dispone de local para atender primeros auxilios", sectores: ['T'] },
  { id: 'MH-26', label: "Existe señal de peligro biológico", sectores: ['S'] },
  { id: 'MH-27', label: "Hay procedimientos de trabajo que minimicen o eviten la diseminación aérea de los agentes biológicos en el lugar de trabajo", sectores: ['S', 'E', 'D'] },
  { id: 'MH-28', label: "Hay procedimientos de trabajo que minimicen o eviten la diseminación de los agentes biológicos en el lugar de trabajo a través de fómites", sectores: ['T'] },
  { id: 'MH-29', label: "Hay procedimientos de gestión de residuos", sectores: ['T'] },
  { id: 'MH-30', label: "Hay procedimientos para el transporte interno de muestras", sectores: ['S', 'E', 'D'] },
  { id: 'MH-31', label: "Hay procedimientos para el transporte externo de muestras", sectores: ['S'] },
  { id: 'MH-32', label: "Hay procedimientos escritos internos para la comunicación de los incidentes donde se puedan liberar agentes biológicos", sectores: ['S'] },
  { id: 'MH-33', label: "Hay procedimientos escritos internos para la comunicación de los accidentes donde se puedan liberar agentes biológicos", sectores: ['S'] },
  { id: 'MH-34', label: "Han recibido los trabajadores la formación requerida por el Real Decreto 664/97", sectores: ['T'] },
  { id: 'MH-35', label: "Han sido informados los trabajadores sobre los aspectos regulados en el Real Decreto 664/97", sectores: ['T'] },
  { id: 'MH-36', label: "Se realiza vigilancia de la salud previa a la exposición de los trabajadores a agentes biológicos", sectores: ['T'] },
  { id: 'MH-37', label: "Se realiza periódicamente vigilancia de la salud", sectores: ['T'] },
  { id: 'MH-38', label: "Hay un registro y control de mujeres embarazadas", sectores: ['T'] },
  { id: 'MH-39', label: "Se toman medidas específicas para el personal especialmente sensible", sectores: ['T'] },
  { id: 'MH-40', label: "¿Se dispone de dispositivos de bioseguridad?", sectores: ['S'] },
  { id: 'MH-41', label: "¿Se utilizan dispositivos adecuados de bioseguridad?", sectores: ['S'] },
  { id: 'MH-42', label: "¿Existen y se utilizan en la empresa procedimientos para el uso adecuado de los dispositivos de bioseguridad?", sectores: ['S'] },
];

// ===== Resultado del cálculo =====
export type NivelRiesgoBiologico = 'aceptable' | 'mejorable' | 'intolerable';

export interface BiologicalRiskResult {
  agent_id: string;
  agent_name: string;
  G: number;
  T: number;
  P: number;
  F: number;
  V: number;
  MH: number;
  R: number;
  nivel: NivelRiesgoBiologico;
}

export const NAB = 8;
export const LEB = 12;

// ===== Funciones de puntuación (PDF sección 2.3 / 2.4) =====

export function puntuarG(grupo: 1 | 2 | 3 | 4): number {
  return grupo;
}

export function puntuarT(vias: ViaTransmision[]): number {
  const puntos: Record<ViaTransmision, number> = { indirecta: 1, directa: 1, aerea: 2 };
  const unicas = Array.from(new Set(vias));
  return unicas.reduce((suma, via) => suma + puntos[via], 0);
}

export function puntuarP(via: ViaProbabilidad, valor: number): number {
  if (via === 'prevalencia') {
    if (valor < 1) return 1;
    if (valor <= 25) return 2;
    if (valor <= 50) return 3;
    return 4;
  }
  // incidencia / 100.000 habitantes
  if (valor < 1) return 1;
  if (valor <= 500) return 2;
  if (valor <= 999) return 3;
  return 4;
}

export function puntuarF(frecuenciaPct: number): number {
  // NOTA: la tabla literal del PDF dice "Frecuentemente 51-80% → 3" y
  // "Habitualmente >80% → 4", pero ambos ejemplos del Anexo II asignan F=4
  // exactamente en 80% ("el personal realiza en el 80% del tiempo... la
  // puntuación... es de 4"). Se replica ese criterio práctico: 80% incluido
  // en el nivel "Habitualmente".
  if (frecuenciaPct < 20) return 1;
  if (frecuenciaPct <= 50) return 2;
  if (frecuenciaPct < 80) return 3;
  return 4;
}

export function puntuarV(vacunacionPct: number): number {
  if (vacunacionPct > 90) return 4;
  if (vacunacionPct >= 70) return 3;
  if (vacunacionPct >= 50) return 2;
  return 1;
}

export function calcularPorcentajeMH(answers: HygieneAnswers): number {
  let afirmativas = 0;
  let negativas = 0;
  for (const respuesta of Object.values(answers)) {
    if (respuesta === 'si') afirmativas++;
    else if (respuesta === 'no') negativas++;
    // 'no_aplicable' se excluye del cálculo, tal como indica el manual (2.4)
  }
  const total = afirmativas + negativas;
  if (total === 0) return 0;
  return (afirmativas / total) * 100;
}

export function puntuarMH(porcentajeAfirmativas: number): number {
  if (porcentajeAfirmativas < 50) return 0;
  if (porcentajeAfirmativas <= 79) return 1;
  if (porcentajeAfirmativas <= 95) return 2;
  return 3;
}

export function nivelDeRiesgo(r: number): NivelRiesgoBiologico {
  if (r <= NAB) return 'aceptable';
  if (r <= LEB) return 'mejorable';
  return 'intolerable';
}

// ===== Función principal =====

export function calcularRiesgoBiologico(
  agent: BiologicalAgent,
  exposure: BiologicalExposureInput,
  porcentajeMH: number
): BiologicalRiskResult {
  const G = puntuarG(agent.grupo);
  const T = puntuarT(agent.vias_transmision);
  const P = puntuarP(exposure.via_probabilidad, exposure.valor_probabilidad);
  const F = puntuarF(exposure.frecuencia_pct);
  const V = puntuarV(exposure.vacunacion_pct);
  const MH = puntuarMH(porcentajeMH);
  const R = G + T + P + F - V - MH;

  return {
    agent_id: agent.id,
    agent_name: agent.name,
    G, T, P, F, V, MH, R,
    nivel: nivelDeRiesgo(R),
  };
}
